import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { Plus, Trash2, Flame, Settings, List, Type, ToggleLeft } from 'lucide-react';
import api from '../../utils/api';

const LeadSettings = () => {
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const [loading, setLoading] = useState(true);

  // New item states
  const [newSource, setNewSource] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [newType, setNewType] = useState('');
  const [fieldName, setFieldName] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [fieldOptions, setFieldOptions] = useState('');
  const [fieldRequired, setFieldRequired] = useState(false);

  const fetchData = async () => {
    try {
      const [sourcesRes, statusesRes, typesRes, customRes] = await Promise.all([
        api('/lead-sources'),
        api('/lead-statuses'),
        api('/lead-types'),
        api('/custom-fields')
      ]);
      
      if (sourcesRes.ok) setSources(await sourcesRes.json());
      if (statusesRes.ok) setStatuses(await statusesRes.json());
      if (typesRes.ok) setLeadTypes(await typesRes.json());
      if (customRes.ok) setCustomFields(await customRes.json());
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddItem = async (endpoint, data, setter, list, clearSetter) => {
    try {
      const response = await api(endpoint, {
        method: 'POST',
        body: data
      });
      if (response.ok) {
        const newItem = await response.json();
        setter([...list, newItem]);
        clearSetter('');
      }
    } catch (error) {
      console.error(`Error adding to ${endpoint}:`, error);
    }
  };

  const handleDeleteItem = async (endpoint, id, setter, list, confirmMsg) => {
    if (window.confirm(confirmMsg || "Delete this item?")) {
      try {
        const response = await api(`${endpoint}/${id}`, { method: 'DELETE' });
        if (response.ok) {
          setter(list.filter(item => item._id !== id));
        }
      } catch (error) {
        console.error(`Error deleting from ${endpoint}:`, error);
      }
    }
  };

  const handleAddCustomField = async (e) => {
    e.preventDefault();
    if (!fieldName.trim()) return;

    const data = {
      name: fieldName,
      type: fieldType,
      options: fieldType === 'select' ? fieldOptions.split(',').map(o => o.trim()).filter(o => o) : [],
      required: fieldRequired
    };

    try {
      const response = await api('/custom-fields', {
        method: 'POST',
        body: data
      });
      if (response.ok) {
        setCustomFields([...customFields, await response.json()]);
        setFieldName('');
        setFieldOptions('');
        setFieldRequired(false);
      }
    } catch (error) {
      console.error("Error adding custom field:", error);
    }
  };

  return (
    <MainLayout title="Lead Settings">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        
        {/* Row 1: Core Lists */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          <SettingsSection 
            title="Lead Sources" 
            items={sources} 
            value={newSource} 
            onChange={setNewSource} 
            onAdd={() => handleAddItem('/lead-sources', { name: newSource }, setSources, sources, setNewSource)}
            onDelete={(id) => handleDeleteItem('/lead-sources', id, setSources, sources)}
            placeholder="e.g. Facebook, Website..."
          />
          
          <SettingsSection 
            title="Lead Statuses" 
            items={statuses} 
            value={newStatus} 
            onChange={setNewStatus} 
            onAdd={() => handleAddItem('/lead-statuses', { name: newStatus }, setStatuses, statuses, setNewStatus)}
            onDelete={(id) => handleDeleteItem('/lead-statuses', id, setStatuses, statuses)}
            placeholder="e.g. New, Contacted..."
          />
        </div>

        {/* Custom Fields Section */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
          {/* Temperature Section */}
          <div>
            <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Flame size={18} color="#D85A30" /> Lead Temperature
              </h3>
              <form onSubmit={(e) => { e.preventDefault(); handleAddItem('/lead-types', { name: newType }, setLeadTypes, leadTypes, setNewType); }} style={{ display: 'flex', gap: '8px' }}>
                <input type="text" value={newType} onChange={(e) => setNewType(e.target.value)} placeholder="Hot, Warm, Cold..." style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }} />
                <button type="submit" style={{ padding: '8px 16px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><Plus size={18} /></button>
              </form>
            </div>
            <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {leadTypes.map(t => (
                  <div key={t._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--color-background-secondary)', borderRadius: '6px', border: '1px solid var(--color-border-tertiary)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Flame size={14} color={t.name.toLowerCase() === 'hot' ? '#D85A30' : (t.name.toLowerCase() === 'warm' ? '#EF9F27' : '#85B7EB')} />
                      <span style={{ fontSize: '14px' }}>{t.name}</span>
                    </div>
                    <button onClick={() => handleDeleteItem('/lead-types', t._id, setLeadTypes, leadTypes)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Custom Fields Section */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Type size={18} /> Create New Lead Field
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)', marginBottom: '16px' }}>
                Add extra fields to your leads. Choose between simple text or a list of options.
              </p>
              <form onSubmit={handleAddCustomField} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Field Name</label>
                  <input type="text" value={fieldName} onChange={(e) => setFieldName(e.target.value)} placeholder="e.g. Project Preference" style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--color-border-primary)' }} />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <label style={{ fontSize: '12px', fontWeight: '600' }}>Field Type</label>
                  <select value={fieldType} onChange={(e) => setFieldType(e.target.value)} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', background: 'white' }}>
                    <option value="text">Single Line Text</option>
                    <option value="textarea">Large Text Box</option>
                    <option value="select">Selection Dropdown (Options)</option>
                  </select>
                </div>

                {fieldType === 'select' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <label style={{ fontSize: '12px', fontWeight: '600' }}>Enter Options</label>
                    <textarea 
                      value={fieldOptions} 
                      onChange={(e) => setFieldOptions(e.target.value)} 
                      placeholder="Type options separated by commas (e.g. Option 1, Option 2, Option 3)" 
                      style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', minHeight: '80px', fontSize: '13px' }}
                    />
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={fieldRequired} onChange={(e) => setFieldRequired(e.target.checked)} />
                  Make this field mandatory
                </label>

                <button type="submit" style={{ padding: '12px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', marginTop: '8px' }}>
                  Add Field to Leads
                </button>
              </form>
            </div>

            <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)' }}>
              <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>Defined Custom Fields</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {customFields.map(f => (
                  <div key={f._id} style={{ padding: '12px', background: 'var(--color-background-secondary)', borderRadius: '6px', border: '1px solid var(--color-border-tertiary)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '600' }}>{f.name}</span>
                      <button onClick={() => handleDeleteItem('/custom-fields', f._id, setCustomFields, customFields, "Delete this field? Lead data for this field will be hidden.")} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={14} /></button>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--color-text-secondary)', display: 'flex', gap: '8px' }}>
                      <span>Type: {f.type}</span>
                      {f.required && <span style={{ color: '#ef4444' }}>(Required)</span>}
                    </div>
                    {f.type === 'select' && (
                      <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>
                        Options: {f.options.join(', ')}
                      </div>
                    )}
                  </div>
                ))}
                {customFields.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: 'var(--color-text-tertiary)', fontSize: '13px' }}>No custom fields defined</div>}
              </div>
            </div>
          </div>
        </div>

        {/* Lead Form Preview Section */}
        <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)', marginTop: '8px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Type size={18} /> Lead Form Preview
          </h3>
          <p style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '24px' }}>
            This preview shows how fields will appear when adding or editing a lead. Hardcoded standard fields are shown alongside your custom fields.
          </p>
          
          <div style={{ padding: '24px', background: 'var(--color-background-secondary)', borderRadius: '8px', border: '1px dashed var(--color-border-tertiary)' }}>
            <h4 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--color-text-secondary)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Standard Fields (Fixed)</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
              <PreviewField label="Full Name *" type="text" placeholder="e.g. Priya Sharma" />
              <PreviewField label="Phone *" type="text" placeholder="98200 XXXXX" />
              <PreviewField label="Email" type="text" placeholder="email@example.com" />
              <PreviewField label="Lead Source" type="select" options={sources.length > 0 ? ['Select source', ...sources.map(s => s.name)] : ['Select source', 'Facebook', 'Website']} />
              <PreviewField label="Stage / Status" type="select" options={statuses.length > 0 ? ['Select status', ...statuses.map(s => s.name)] : ['Select status', 'New', 'Contacted']} />
              <PreviewField label="Temperature" type="select" options={leadTypes.length > 0 ? ['Select temperature', ...leadTypes.map(t => t.name)] : ['Select temperature', 'Hot', 'Warm']} />
            </div>

            {customFields.length > 0 && (
              <>
                <h4 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--color-brand-primary)', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: '1px dashed var(--color-border-tertiary)', paddingTop: '24px' }}>Your Custom Fields</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {customFields.map(field => (
                    <PreviewField 
                      key={field._id}
                      label={`${field.name}${field.required ? ' *' : ''}`}
                      type={field.type}
                      options={field.type === 'select' ? ['Select an option', ...field.options] : []}
                      placeholder={field.type === 'textarea' ? 'Enter text...' : `Enter ${field.name.toLowerCase()}...`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

const PreviewField = ({ label, type, placeholder, options }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-text-secondary)' }}>{label}</label>
    {type === 'select' ? (
      <select disabled style={{ padding: '10px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', background: 'white', color: 'var(--color-text-tertiary)' }}>
        {options.map((opt, i) => (
          <option key={i}>{opt}</option>
        ))}
      </select>
    ) : type === 'textarea' ? (
      <textarea disabled placeholder={placeholder} style={{ padding: '10px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', background: 'white', color: 'var(--color-text-tertiary)', minHeight: '60px' }} />
    ) : (
      <input disabled type="text" placeholder={placeholder} style={{ padding: '10px 12px', borderRadius: 'var(--border-radius-md)', border: '1px solid var(--color-border-primary)', fontSize: '13px', background: 'white', color: 'var(--color-text-tertiary)' }} />
    )}
  </div>
);

const SettingsSection = ({ title, items, value, onChange, onAdd, onDelete, placeholder }) => (
  <div>
    <div className="card" style={{ background: 'var(--color-background-primary)', padding: '24px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)', marginBottom: '12px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <List size={16} /> {title}
      </h3>
      <form onSubmit={(e) => { e.preventDefault(); onAdd(); }} style={{ display: 'flex', gap: '8px' }}>
        <input 
          type="text" 
          value={value} 
          onChange={(e) => onChange(e.target.value)} 
          placeholder={placeholder} 
          style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--color-border-primary)', fontSize: '13px' }} 
        />
        <button type="submit" style={{ padding: '8px 12px', background: 'var(--color-brand-primary)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><Plus size={18} /></button>
      </form>
    </div>
    <div className="card" style={{ background: 'var(--color-background-primary)', padding: '16px', borderRadius: 'var(--border-radius-lg)', border: '1px solid var(--color-border-tertiary)', maxHeight: '250px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {items.map(item => (
          <div key={item._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--color-background-secondary)', borderRadius: '6px', border: '1px solid var(--color-border-tertiary)' }}>
            <span style={{ fontSize: '13px' }}>{item.name}</span>
            <button onClick={() => onDelete(item._id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer' }}><Trash2 size={13} /></button>
          </div>
        ))}
        {items.length === 0 && <div style={{ textAlign: 'center', padding: '10px', color: 'var(--color-text-tertiary)', fontSize: '12px' }}>Empty</div>}
      </div>
    </div>
  </div>
);

export default LeadSettings;
